// CalendarSearchBox.tsx — Backend-Suche im Kalender-Neubau (`/termine`, SP5).
// Debounced Eingabe (search/debouncedSearch aus dem Context, 300ms), die den
// rangeübergreifenden Such-Endpoint (calendarService.searchEvents → POST
// /calendar/search) anstößt. Ergebnisse erscheinen in einem eigenen Popover
// (RdPopover); Klick auf einen Treffer springt zum Termindatum (setCurrentDate +
// view=day) und schließt das Popover. Die Suche ist NICHT in den Grid-Event-Load
// gemerged (eigener Pfad, Datenfluss §6).

import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { REFINED } from "../risikoanalyseStep/tokens";
import { RdPopover } from "../redesign/table/RdPopover";
import { useCalendarState } from "./CalendarStateContext";
import { calendarService, type SourcedCalendarEvent } from "../../services/calendarService";
import { toGraphDate } from "../dashboard/DashboardShared";

interface SearchHit {
    id: string;
    title: string;
    start: Date;
    color: string;
    sourceName: string;
}

const CalendarSearchBox: React.FC = () => {
    const { search, setSearch, debouncedSearch, calendarSources, setCurrentDate, setView } =
        useCalendarState();
    const [hits, setHits] = useState<SearchHit[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const reqIdRef = useRef(0);

    // Backend-Suche bei jeder debounced Term-Änderung. Race-sicher über eine
    // monoton steigende Request-Id (nur die jüngste Antwort gewinnt).
    useEffect(() => {
        const term = debouncedSearch.trim();
        const myReq = ++reqIdRef.current;
        if (term.length === 0 || calendarSources.length === 0) {
            setHits([]);
            setLoading(false);
            setError(false);
            return;
        }
        setLoading(true);
        setError(false);
        calendarService
            .searchEvents(calendarSources, term, { top: 25 })
            .then((events: SourcedCalendarEvent[]) => {
                if (myReq !== reqIdRef.current) return;
                const mapped = events
                    .map<SearchHit>((ev) => ({
                        id: ev.id,
                        title: ev.subject || "(ohne Titel)",
                        start: toGraphDate(ev.start.dateTime),
                        color: ev.source.color,
                        sourceName: ev.source.displayName,
                    }))
                    .sort((a, b) => a.start.getTime() - b.start.getTime());
                setHits(mapped);
                setLoading(false);
            })
            .catch((err) => {
                if (myReq !== reqIdRef.current) return;
                console.warn("[CalendarSearchBox] Suche fehlgeschlagen", err);
                setHits([]);
                setError(true);
                setLoading(false);
            });
    }, [debouncedSearch, calendarSources]);

    const term = debouncedSearch.trim();
    const showPopover = term.length > 0;

    return (
        <RdPopover
            width={340}
            trigger={({ toggle, open, ref }) => (
                // RdPopover positioniert relativ zum Trigger-Ref und prüft
                // Außenklicks über .contains() — daher den Ref auf den ganzen
                // Box-Container (inkl. Input + Clear) hängen, damit ein Klick ins
                // Feld nicht als Außenklick zählt. Beide nur DOM-Methoden, der
                // Cast (Button→Div) ist zur Laufzeit unbedenklich.
                <div ref={ref as unknown as React.Ref<HTMLDivElement>} style={boxStyle(open)}>
                    <span aria-hidden style={{ color: REFINED.ink3 }}>
                        🔍
                    </span>
                    <input
                        type="text"
                        value={search}
                        placeholder="Termine durchsuchen…"
                        onChange={(e) => {
                            setSearch(e.target.value);
                            if (!open && e.target.value.trim().length > 0) toggle();
                        }}
                        onFocus={() => {
                            if (!open && showPopover) toggle();
                        }}
                        style={inputStyle}
                        autoComplete="off"
                    />
                    {search.length > 0 && (
                        <button
                            type="button"
                            aria-label="Suche löschen"
                            onClick={() => {
                                setSearch("");
                                if (open) toggle();
                            }}
                            style={clearBtnStyle}
                        >
                            ×
                        </button>
                    )}
                </div>
            )}
        >
            {(close) =>
                showPopover ? (
                    <div style={panelStyle}>
                        {loading && <div style={infoStyle}>Suche läuft…</div>}
                        {!loading && error && (
                            <div style={infoStyle}>Suche derzeit nicht möglich.</div>
                        )}
                        {!loading && !error && hits.length === 0 && (
                            <div style={infoStyle}>Keine Treffer.</div>
                        )}
                        {!loading &&
                            !error &&
                            hits.map((h) => (
                                <button
                                    key={`${h.id}`}
                                    type="button"
                                    onClick={() => {
                                        setCurrentDate(new Date(h.start));
                                        setView("day");
                                        close();
                                    }}
                                    style={hitStyle}
                                >
                                    <span
                                        aria-hidden
                                        style={{
                                            width: 8,
                                            alignSelf: "stretch",
                                            borderRadius: 4,
                                            background: h.color,
                                            flex: "0 0 auto",
                                        }}
                                    />
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                        <span style={hitTitleStyle}>{h.title}</span>
                                        <span style={hitMetaStyle}>
                                            {moment(h.start).format("ddd, DD.MM.YYYY HH:mm")} ·{" "}
                                            {h.sourceName}
                                        </span>
                                    </span>
                                </button>
                            ))}
                    </div>
                ) : null
            }
        </RdPopover>
    );
};

const boxStyle = (open: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 36,
    padding: "0 10px",
    borderRadius: 8,
    border: `1px solid ${open ? REFINED.aub : REFINED.line}`,
    background: REFINED.fieldBg,
    boxShadow: open ? REFINED.focusRing : "none",
    minWidth: 220,
});

const inputStyle: React.CSSProperties = {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    color: REFINED.ink,
    font: `400 14px ${REFINED.fontUI}`,
    minWidth: 0,
};

const clearBtnStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    color: REFINED.ink3,
    font: `700 18px ${REFINED.fontUI}`,
    cursor: "pointer",
    lineHeight: 1,
    padding: 0,
};

const panelStyle: React.CSSProperties = {
    background: REFINED.white,
    border: `1px solid ${REFINED.line}`,
    borderRadius: 10,
    boxShadow: REFINED.cardShadow,
    padding: 6,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxHeight: 380,
    overflowY: "auto",
};

const infoStyle: React.CSSProperties = {
    padding: "10px 8px",
    color: REFINED.ink3,
    font: `400 13px ${REFINED.fontUI}`,
};

const hitStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "stretch",
    gap: 10,
    minHeight: 48,
    padding: "6px 8px",
    borderRadius: 8,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
};

const hitTitleStyle: React.CSSProperties = {
    display: "block",
    color: REFINED.ink,
    font: `600 14px ${REFINED.fontUI}`,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const hitMetaStyle: React.CSSProperties = {
    display: "block",
    color: REFINED.ink3,
    font: `400 12px ${REFINED.fontUI}`,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

export default CalendarSearchBox;
